import { createRouter, createWebHistory } from "vue-router";
import { pageView } from "@/services/analytics";

const APP_NAME = "Abood Bus";
// Canonical absolute origin — used to assemble canonical URLs, OG
// `url`, and hreflang link tags from the current route's path.
const SITE_ORIGIN = "https://aboodbus.co.tz";
const OG_DEFAULT_IMAGE = `${SITE_ORIGIN}/images/og-cover.jpg`;

// Per-route `meta` carries the title + description applied by the
// `afterEach` guard below. Search engines + WhatsApp/Twitter card
// previews surface per-page titles instead of the static one in
// index.html.
const routes = [
  {
    path: "/", name: "Home",
    component: () => import("@/views/HomePage.vue"),
    meta: {
      title: "Book bus tickets across Tanzania",
      description: "Premium intercity bus service. Search routes, pick your seat, and pay with mobile money — in under a minute. Trusted by 2M+ passengers since 1986.",
      breadcrumb: [],
      noindex: false,
    },
  },
  {
    path: "/schedules-journey",
    name: "journeyPage",
    component: () => import("@/components/Journey"),
    meta: {
      title: "Available trips",
      description: "Live bus schedules across Tanzania. Compare departure times, available seats, and fares — book in two taps.",
      breadcrumb: [{ name: "Trips" }],
    },
  },
  {
    path: "/passenger-booking-details",
    name: "BookingDetails",
    component: () => import("@/components/BookDetails.vue"),
    meta: {
      title: "Passenger details",
      description: "Enter passenger information to continue booking.",
      breadcrumb: [{ name: "Trips", path: "/schedules-journey" }, { name: "Passenger details" }],
      noindex: true,
    },
  },
  {
    path: "/passenger-payments",
    name: "Payments",
    component: () => import("@/components/Payments.vue"),
    meta: {
      title: "Payment",
      description: "Pay for your tickets with mobile money or card.",
      breadcrumb: [{ name: "Trips", path: "/schedules-journey" }, { name: "Passenger details", path: "/passenger-booking-details" }, { name: "Payment" }],
      noindex: true,
    },
  },
  {
    path: "/seat-details",
    name: "SeatDetails",
    component: () => import("@/components/SeatDetails.vue"),
    meta: {
      title: "Your tickets",
      description: "Booking confirmation and ticket details.",
      noindex: true,
    },
  },
  {
    path: "/ticket",
    name: "Tickets",
    component: () => import("@/views/accounts/UserTicketsPage.vue"),
    meta: { title: "My tickets", noindex: true, requiresAuth: true, hideMarketing: true },
  },
  {
    path: "/user-profile",
    name: "Profile",
    component: () => import("@/views/accounts/UserProfilePage.vue"),
    meta: { title: "My profile", noindex: true, requiresAuth: true, hideMarketing: true },
  },
  {
    /*
     * Changing a ticket WITHOUT an account.
     *
     * Deliberately public — the passenger this exists for is the one who
     * booked before they registered, so requiresAuth would lock out exactly
     * the person it is meant to serve. Proof of identity is the SMS code, not
     * a session.
     *
     * noindex because the page is only useful to someone holding a booking
     * reference; there is nothing here for a search engine.
     */
    path: "/change-ticket",
    name: "GuestAmendment",
    component: () => import("@/views/accounts/GuestAmendmentPage.vue"),
    meta: { title: "Change or cancel a ticket", noindex: true, hideMarketing: true },
  },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      const section = document.querySelector(to.hash);
      if (section) {
        return {
          el: to.hash,
          behavior: "smooth",
        };
      }
    }
    return { top: 0 };
  },
});
router.beforeEach((to, from, next) => {
  if (from.name === "SeatDetails" && to.name !== "Home") {
    next({ name: "Home" });
    return;
  }

  /*
   * Account pages need a session.
   *
   * Without this they rendered for anyone, then fired authenticated calls that
   * came back 401 — so a signed-out visitor got an empty, broken page instead
   * of being asked to sign in. Reading the token from localStorage rather than
   * the store because the guard can run before the store has rehydrated on a
   * cold load.
   *
   * `redirect` is carried so the sign-in flow can return them where they were
   * headed rather than dropping them on the home page.
   */
  if (to.meta?.requiresAuth) {
    let token = null;

    try {
      token = localStorage.getItem("auth_token");
    } catch (e) {
      // Private mode / storage disabled — treat as signed out.
      token = null;
    }

    if (!token) {
      next({ path: "/", query: { signin: "1", redirect: to.fullPath } });
      return;
    }
  }

  next();
});

/**
 * Per-route SEO orchestrator. Touches:
 *   - document.title
 *   - meta[name=description]
 *   - link[rel=canonical]
 *   - meta[name=robots]               (noindex toggle for state-y routes)
 *   - link[rel=alternate hreflang]    (en / sw / x-default)
 *   - OG + Twitter card metadata
 *   - script[type=application/ld+json data-route="breadcrumb"]
 *
 * Helpers live inside the guard so the closure can keep one
 * "set-or-create" implementation and we avoid leaking generic DOM
 * utilities to the rest of the codebase. The cost is rebuild-on-
 * call, which is negligible.
 */
router.afterEach((to) => {
  if (typeof document === "undefined") return;

  const title = to.meta?.title
    ? `${to.meta.title} · ${APP_NAME}`
    : APP_NAME;
  const desc =
    to.meta?.description || `${APP_NAME} — book bus tickets across Tanzania.`;
  const path = to.fullPath || to.path;
  // Strip the trailing `/` so canonicals don't end up as `/foo/` on
  // some routes and `/foo` on others — duplicate-content risk.
  const canonical =
    SITE_ORIGIN + (path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path);

  document.title = title;

  // ── set-or-create helpers ────────────────────────────────────────
  const setMeta = (attr, key, value) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", value);
  };
  const setLink = (key, hreflang, href) => {
    const sel = hreflang
      ? `link[rel="${key}"][hreflang="${hreflang}"]`
      : `link[rel="${key}"]`;
    let el = document.querySelector(sel);
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", key);
      if (hreflang) el.setAttribute("hreflang", hreflang);
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
  };

  // ── Base meta ────────────────────────────────────────────────────
  setMeta("name", "description", desc);

  // ── Canonical + hreflang ─────────────────────────────────────────
  // Same path across languages — Vue-i18n stores the locale client-
  // side, not in the URL. We add `?lang=` query alternates so
  // crawlers can fetch the localised version of the same page.
  setLink("canonical", "", canonical);
  const sep = canonical.includes("?") ? "&" : "?";
  setLink("alternate", "en",       `${canonical}${sep}lang=en`);
  setLink("alternate", "sw",       `${canonical}${sep}lang=sw`);
  setLink("alternate", "x-default", canonical);

  // ── Robots ──────────────────────────────────────────────────────
  // State-y routes (post-search, post-login) get noindex so empty
  // shells don't end up in the index.
  setMeta(
    "name",
    "robots",
    to.meta?.noindex ? "noindex, nofollow" : "index, follow"
  );

  // ── OG / Twitter ────────────────────────────────────────────────
  setMeta("property", "og:title",       title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:type",        "website");
  setMeta("property", "og:url",         canonical);
  setMeta("property", "og:image",       OG_DEFAULT_IMAGE);
  setMeta("property", "og:image:width", "1200");
  setMeta("property", "og:image:height","630");
  setMeta("property", "og:site_name",   APP_NAME);
  setMeta("name",     "twitter:card",   "summary_large_image");
  setMeta("name",     "twitter:title",  title);
  setMeta("name",     "twitter:description", desc);
  setMeta("name",     "twitter:image",  OG_DEFAULT_IMAGE);

  // ── BreadcrumbList JSON-LD (per route) ──────────────────────────
  // Removes the previous breadcrumb script before writing a new one
  // so the page never has two stacked.
  document.querySelectorAll('script[data-route="breadcrumb"]').forEach((s) => s.remove());
  const crumbs = Array.isArray(to.meta?.breadcrumb) ? to.meta.breadcrumb : null;
  if (crumbs && crumbs.length) {
    const itemListElement = [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
      ...crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 2,
        name: c.name,
        ...(c.path ? { item: `${SITE_ORIGIN}${c.path}` } : {}),
      })),
    ];
    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.setAttribute("data-route", "breadcrumb");
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement,
    });
    document.head.appendChild(breadcrumbScript);
  }

  // ── GA4 SPA page view ───────────────────────────────────────────
  // No-op when GA isn't configured. The very first page view is
  // dispatched from `initAnalytics()` in main.js (it has to land
  // AFTER `gtag('config')` runs, otherwise gtag drops it).
  pageView(to.fullPath, title);
});

export default router;
